import { useState } from "react";
import { X } from "lucide-react";
import type { InvoiceUploadInput } from "@shared-core/data/supplier-portal-adapter";
import { useFieldErrorWrite } from "@shared-core/domain/useFieldErrorWrite";
import {
  ACTION_WORDING,
  INVOICE_WORDING,
} from "@shared-core/domain/supplier-portal-wording";
import { Button } from "../primitives/button";
import { Input } from "../primitives/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../primitives/select";
import { FilePicker } from "../patterns/FilePicker";
import { FormField } from "../patterns/FormField";

/**
 * Upload an invoice against one or more purchase orders (brief decision 5): the PDF, which orders, the supplier's
 * own invoice number, the total inc VAT and the invoice date. It goes into the same purchase invoice intake finance already
 * uses. On a purchase order's page the order is fixed; on the Invoices page the supplier chooses from `choices`.
 */
export function InvoiceUploadForm({
  choices,
  fixedPurchaseOrderNo = null,
  today,
  onSubmit,
  onUploaded,
  idPrefix = "invoice",
}: {
  /** The purchase orders an invoice can be uploaded against now. */
  choices: string[];
  fixedPurchaseOrderNo?: string | null;
  today: string;
  onSubmit: (input: InvoiceUploadInput) => Promise<void>;
  onUploaded?: () => void;
  idPrefix?: string;
}) {
  const initialOrders = fixedPurchaseOrderNo
    ? [fixedPurchaseOrderNo]
    : choices.slice(0, 1);
  const [file, setFile] = useState<File | null>(null);
  const [orders, setOrders] = useState<string[]>(initialOrders);
  const [invoiceRef, setInvoiceRef] = useState("");
  const [total, setTotal] = useState("");
  const [invoiceDate, setInvoiceDate] = useState(today);
  const [isSent, setIsSent] = useState(false);
  const write = useFieldErrorWrite("We couldn't send your invoice.");
  const errors = write.fieldErrors;
  const id = (field: string) => `${idPrefix}-${field}`;
  const addable = choices.filter((no) => !orders.includes(no));

  const submit = async () => {
    setIsSent(false);
    const ok = await write.run(() =>
      onSubmit({
        file,
        purchase_order_nos: orders,
        invoice_ref: invoiceRef,
        total_inc_vat: total,
        invoice_date: invoiceDate,
      }),
    );
    if (!ok) return;
    setFile(null);
    setOrders(initialOrders);
    setInvoiceRef("");
    setTotal("");
    setInvoiceDate(today);
    setIsSent(true);
    onUploaded?.();
  };

  return (
    <form
      noValidate
      aria-label={INVOICE_WORDING.uploadTitle}
      className="grid gap-3"
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
    >
      {write.banner && (
        <p role="alert" className="text-sm text-red-600">
          {write.banner}
        </p>
      )}
      {isSent && (
        <p role="status" className="text-sm font-semibold text-green-700">
          ✓ {INVOICE_WORDING.sent}
        </p>
      )}
      <FormField id={id("file")} label="Invoice PDF" error={errors.file}>
        {(control) => (
          <FilePicker
            {...control}
            file={file}
            onChange={setFile}
            accept="application/pdf"
            prompt={INVOICE_WORDING.drop}
            chooseLabel={INVOICE_WORDING.chooseFile}
          />
        )}
      </FormField>
      <FormField
        id={id("orders")}
        label={INVOICE_WORDING.forPurchaseOrders}
        error={errors.purchase_order_nos}
      >
        {(control) =>
          fixedPurchaseOrderNo ? (
            <Input {...control} value={fixedPurchaseOrderNo} readOnly />
          ) : (
            <div className="grid gap-2">
              {orders.length > 0 && (
                <ul
                  className="flex flex-wrap gap-1.5"
                  aria-label="Chosen purchase orders"
                >
                  {orders.map((no) => (
                    <li
                      key={no}
                      className="inline-flex items-center gap-1 rounded-md border bg-card px-2 py-0.5 font-mono text-xs"
                    >
                      {no}
                      <button
                        type="button"
                        className="text-muted-foreground hover:text-foreground"
                        aria-label={`Remove ${no}`}
                        onClick={() =>
                          setOrders((current) =>
                            current.filter((x) => x !== no),
                          )
                        }
                      >
                        <X className="size-3" aria-hidden />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {addable.length > 0 && (
                <Select
                  value=""
                  onValueChange={(no) =>
                    setOrders((current) => [...current, no])
                  }
                >
                  <SelectTrigger {...control} className="w-full">
                    <SelectValue
                      placeholder={
                        orders.length
                          ? "Add another purchase order"
                          : "Choose a purchase order"
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {addable.map((no) => (
                      <SelectItem key={no} value={no}>
                        {no}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          )
        }
      </FormField>
      <FormField
        id={id("ref")}
        label={INVOICE_WORDING.invoiceNumber}
        error={errors.invoice_ref}
      >
        {(control) => (
          <Input
            {...control}
            value={invoiceRef}
            placeholder={INVOICE_WORDING.invoiceNumberPlaceholder}
            onChange={(event) => setInvoiceRef(event.target.value)}
          />
        )}
      </FormField>
      <div className="grid grid-cols-2 gap-3">
        <FormField
          id={id("total")}
          label={INVOICE_WORDING.total}
          error={errors.total_inc_vat}
        >
          {(control) => (
            <Input
              {...control}
              inputMode="decimal"
              placeholder="£"
              value={total}
              onChange={(event) => setTotal(event.target.value)}
            />
          )}
        </FormField>
        <FormField
          id={id("date")}
          label={INVOICE_WORDING.invoiceDate}
          error={errors.invoice_date}
        >
          {(control) => (
            <Input
              {...control}
              type="date"
              max={today}
              value={invoiceDate}
              onChange={(event) => setInvoiceDate(event.target.value)}
            />
          )}
        </FormField>
      </div>
      <Button
        type="submit"
        className="justify-self-start"
        disabled={write.isBusy}
      >
        {ACTION_WORDING.sendInvoice}
      </Button>
    </form>
  );
}
